/**
 * W-c72720ce: the World's refusals of a managed Codex step, written for
 * people. The World answers a step it will not run with
 * {"error":{"code":"managed_codex_<x>","message":"<sentence>"}}
 * (backend/convex/managedCodexHttp.js). Codex reports a 400 as that raw body,
 * and a status it retries (402, 403 and 409 before #1082; 5xx), once its
 * retries end, as "unexpected status 402 Payment Required: <sentence>"
 * (codex-cli 0.160.0 against a local fake endpoint, 5 Oct 2026).
 *
 * Only the codes below reach a person, and only their message. Every other
 * error (another provider's body, the runtime's own bridge refusals, a
 * transport failure) is not the World's sentence and keeps its own line.
 * Each code lists the fixed sentences the World sends with it, so a report
 * that carries only the sentence is recognised; the input limit's sentence
 * names the request's own counts and is read only from a body with its code.
 */
export const MANAGED_CODEX_WORLD_REFUSALS: Readonly<Record<string, ReadonlyArray<string>>> = {
  managed_codex_insufficient_credits: [
    "This World needs more usage credits before Cleo can run this request.",
  ],
  managed_codex_refused: ["This request is not authorized for its current Cleo conversation."],
  managed_codex_model_unavailable: ["This model is not currently available for this World."],
  managed_codex_stopped: ["This message was stopped. You can send another message."],
  managed_codex_stopping: [
    "Cleo is still releasing the credit held by the message you stopped. This asks again by itself.",
  ],
  managed_codex_not_started: [
    "This request did not start. Its reserved credits were released; you can send another message.",
  ],
  managed_codex_message_limit: [
    "Cleo paused before the next step because it could exceed this message’s spending limit. You can send a follow-up message.",
  ],
  // #1125: a step stopped at an output cap fitted to what it may hold. The World's sentence names the room left and
  // every limit that set the cap, read from a body with its code; the fixed one stands when only the sentence arrives.
  managed_codex_stopped_at_credit_limit: [
    "Cleo stopped this step at the credit limit. Add usage credits, then send again.",
  ],
  managed_codex_stopped_at_budget: [
    "Cleo stopped this step at the monthly budget. A founder can raise the budget in Usage, then send again.",
  ],
  managed_codex_stopped_at_message_limit: [
    "Cleo stopped this step at this message's spending limit. Send what is left as a new message.",
  ],
  managed_codex_input_limit: [
    "This conversation is larger than Neucleos accepts for this model in one request. Start a new thread, then send again.",
  ],
  managed_codex_request_size_refused: [
    "Cleo could not safely continue this conversation. Its completed steps are saved.",
  ],
  managed_codex_text_size_refused: [
    "Cleo could not safely continue this conversation. Its completed steps are saved.",
  ],
  managed_codex_native_contract_refused: [
    "Cleo could not safely continue this conversation. Its completed steps are saved.",
  ],
  managed_codex_commitment_refused: [
    "Cleo could not safely continue this conversation. Its completed steps are saved.",
  ],
  managed_codex_continuation_refused: [
    "Cleo could not safely continue this conversation. Its completed steps are saved.",
  ],
  managed_codex_request_refused: [
    "Cleo cannot run this request with the selected model, message and usage limit.",
  ],
  managed_codex_request_invalid: [
    "Cleo could not read this bounded request.",
    "Cleo could not read this Stop request.",
  ],
  managed_codex_uncertain: [
    "Cleo could not confirm this request. Any uncertain charge remains reserved; retry will not send another paid request.",
  ],
  managed_codex_in_flight: [
    "Cleo is still finishing this step. Nothing was sent or charged again; its result arrives when it is ready.",
  ],
  managed_codex_connection_unavailable: ["Cleo's model connection needs attention."],
  managed_codex_count_unavailable: [
    "Cleo could not check the cost of the next step. No additional model request was sent.",
  ],
  managed_codex_provider_refused: [
    "The provider refused this request. Its unused credit hold was released.",
  ],
  managed_codex_incomplete_tool: [
    "Cleo could not finish preparing this action. No partial action was sent.",
  ],
  managed_codex_empty_response: ["Cleo finished without an answer. You can send another message."],
};

const FIXED_SENTENCES: ReadonlySet<string> = new Set(
  Object.values(MANAGED_CODEX_WORLD_REFUSALS).flat(),
);
// The longest report read; a World sentence is a few hundred characters.
const MAX_REPORT_CHARS = 4_000;
// Codex's prefix for a status it gave up retrying, and the suffixes some
// versions add after the body's message.
const STATUS_PREFIX = /^unexpected status \d{3}(?: [A-Za-z' -]{1,60})?: /u;
const CODEX_SUFFIX = /, (?:url|cf-ray|request id): [\s\S]*$/u;
// The 0.0.184 runtime keeps 180 characters of a runtime error on its
// activity (ProviderRuntimeIngestion.ts truncateDetail), ending in "...".
const CUT_BODY = /^\{"error":\{"code":"(managed_codex_[a-z_]{1,64})","message":"([\s\S]*)\.\.\.$/u;

function isAllowedCode(code: unknown): code is string {
  return typeof code === "string" && Object.hasOwn(MANAGED_CODEX_WORLD_REFUSALS, code);
}

function plainSentence(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const sentence = value.trim();
  return sentence.length > 0 && !/\p{Cc}/u.test(sentence) ? sentence : undefined;
}

type Reading =
  | { readonly kind: "whole"; readonly code: string | null; readonly sentence: string }
  | { readonly kind: "cut"; readonly code: string; readonly start: string };

function readBody(text: string): Reading | undefined {
  if (!text.startsWith("{")) return undefined;
  try {
    const body: unknown = JSON.parse(text);
    const error =
      body !== null && typeof body === "object" ? (body as { error?: unknown }).error : undefined;
    if (error === null || typeof error !== "object") return undefined;
    const { code, message } = error as { code?: unknown; message?: unknown };
    const sentence = plainSentence(message);
    return isAllowedCode(code) && sentence !== undefined
      ? { kind: "whole", code, sentence }
      : undefined;
  } catch {
    const cut = CUT_BODY.exec(text);
    if (!cut || !isAllowedCode(cut[1])) return undefined;
    // The cut may fall inside an escape; drop a trailing partial one.
    let start = cut[2]!;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const decoded: unknown = JSON.parse(`"${start}"`);
        return typeof decoded === "string" && decoded.trim().length > 0
          ? { kind: "cut", code: cut[1], start: decoded.trimStart() }
          : undefined;
      } catch {
        start = start.slice(0, -1);
      }
    }
    return undefined;
  }
}

function read(text: unknown): Reading | undefined {
  if (typeof text !== "string" || text.length > MAX_REPORT_CHARS) return undefined;
  let report = text.trim();
  const prefixed = STATUS_PREFIX.exec(report);
  if (prefixed) report = report.slice(prefixed[0].length).trim();
  const body = readBody(report);
  if (body !== undefined) return body;
  if (report.startsWith("{")) {
    // A body followed by one of Codex's suffixes.
    const end = report.lastIndexOf("}");
    return end > 0 ? readBody(report.slice(0, end + 1)) : undefined;
  }
  // Only a sentence the World sends with one of its codes.
  const sentence = prefixed ? report.replace(CODEX_SUFFIX, "").trim() : report;
  return FIXED_SENTENCES.has(sentence) ? { kind: "whole", code: null, sentence } : undefined;
}

/**
 * The World's sentence when `report` is the World refusing a managed Codex
 * step (its raw body, or Codex's "unexpected status" report of it);
 * otherwise undefined. A body cut short is completed only from `uncut`, an
 * uncut report of the same refusal (the conversation's last error), or from
 * the one fixed sentence the World sends with that code that it starts.
 */
export function managedCodexRefusalSentence(report: unknown, uncut?: unknown): string | undefined {
  const reading = read(report);
  if (reading === undefined) return undefined;
  if (reading.kind === "whole") return reading.sentence;
  const whole = read(uncut);
  if (
    whole?.kind === "whole" &&
    (whole.code === reading.code ||
      (whole.code === null &&
        MANAGED_CODEX_WORLD_REFUSALS[reading.code]!.includes(whole.sentence))) &&
    whole.sentence.startsWith(reading.start)
  )
    return whole.sentence;
  const fixed = MANAGED_CODEX_WORLD_REFUSALS[reading.code]!.filter((sentence) =>
    sentence.startsWith(reading.start),
  );
  return fixed.length === 1 ? fixed[0] : undefined;
}
